// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Notify from "@vanmeter/App/Components/Notify";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { xIsAuthenticated } from "@vanmeter/App/Store/Selectors/GeneralSelectors";
import { ProductPartNumbersStyles } from "@vanmeter/Overrides/Components/ProductPartNumbers";
import React, { FC, Fragment } from "react";
import { connect } from "react-redux";

interface OwnProps {
    showLabel?: boolean;
    styles: ProductPartNumbersStyles;
    cpnOverride?: string;
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        cpnData: state.xPartNumbers.data,
        isAuth: xIsAuthenticated(state),
    };
};

type Props = OwnProps & HasProductContext & HasCartLineContext & ReturnType<typeof mapStateToProps>;

const CustomCpn: FC<Props> = ({
    isAuth,
    showLabel = true,
    cpnData,
    productContext,
    cartLine,
    styles,
    // XNG-Changes: cpnOverride to be received from parent component (orderLine, InvoiceLine, Rma, Requisitions)
    cpnOverride,
}) => {
    const productId = productContext?.product?.id || cartLine?.productId;
    const customerPartNumber = cpnData[productId || ""];
    if (!isAuth) {
        return null;
    }

    if (!IS_PRODUCTION && !productId && cpnOverride === undefined) {
        return <Notify type="danger">{translate("CPN: Product Context or Cart Line Context is missing")}</Notify>;
    }

    if (!customerPartNumber && !cpnOverride) {
        return null;
    }

    return (
        <>
            {showLabel && <Typography {...styles.customerNameLabelText}>{translate("CPN")}</Typography>}
            {!showLabel && (
                <VisuallyHidden>
                    <Typography {...styles.customerNameLabelText}>{translate("CPN")}</Typography>
                </VisuallyHidden>
            )}
            <StyledWrapper {...styles.cpnValueWrapper}>
                <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                    {cpnOverride ??
                        customerPartNumber.split(",").map((number, i) => (
                            <Fragment key={i}>
                                {number}
                                <br />
                            </Fragment>
                        ))}
                </Typography>
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps)(withCartLine(withProductContext(CustomCpn)));
